import React, { useContext, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import Axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import {
  Button, Grid, Drawer, Box, Typography,
  IconButton,
  CircularProgress,
  Tooltip
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import { Save } from '@mui/icons-material';

import { appendContactSupport, axiosHeaders, getLocalisedErrorString } from 'src/utils/helpers';
import { useAuth } from 'src/hooks/useAuth';
import ConfigContext from 'src/contexts/ConfigContext';
import { vatLineFields, esLineFields } from 'src/config'
import styles from '../style';


const NotifyProcessedDoc = () => {
  const { API } = useContext(ConfigContext);
  const { ready, t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();

  const headerFields = useSelector((state) => state.fields?.headerFields || []);
  const lineFields = useSelector((state) => state.fields?.lineFields || []);

  const [loading, setLoading] = useState(false);
  const [appError, setAppError] = useState(false);
  const [isFieldChanged, setIsFieldChanged] = useState(false);

  const [config, setConfig] = useState({
    method: 'POST',
    url: '',
    authorizationType: 'basic-auth',
    username: '',
    password: '',
    bearerToken: '',
    key: '',
    value: ''
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [showApiValue, setShowApiValue] = useState(false);
  const [openExampleDrawer, setOpenExampleDrawer] = useState(false);
  const [exampleRequest, setExampleRequest] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);

  useEffect(() => {
    getSavedDetails();
  }, []);

  const getSavedDetails = async () => {
    setLoading(true);
    try {
      const response = await Axios.get(
        `${API.integrations}/processed/api`, axiosHeaders(localStorage.getItem('PROCYS_accessToken'))
      );
      if (response.data.success) {
        let responseData = response.data.data;
        if (!responseData || typeof responseData !== 'object' || Object.keys(responseData).length === 0) {
          responseData = {
            method: 'POST',
            url: '',
            authorizationType: 'basic-auth',
            username: '',
            password: '',
            bearerToken: '',
            key: '',
            value: ''
          };
        } else {
          responseData = {
            method: responseData.method || 'POST',
            url: responseData.url || '',
            authorizationType: responseData.authorizationType || 'basic-auth',
            username: responseData.username || '',
            password: responseData.password || '',
            bearerToken: responseData.bearerToken || '',
            key: responseData.key || '',
            value: responseData.value || ''
          };
        }
        setConfig(responseData);
        setLoading(false);
      } else {
        setAppError(t('CUSTOMER_INTEGRATIONS_ERROR_RETRIEVE_DETAILS'));
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
      setAppError(t('CUSTOMER_INTEGRATIONS_ERROR_RETRIEVE_DETAILS'));
    }
  };

  const handleConfigChange = (event) => {
    const { name, value } = event.target;
    setConfig({
      ...config,
      [name]: value
    });
    setIsFieldChanged(true);
  };

  const saveDetails = async () => {
    if (!config) {
      enqueueSnackbar(
        t('CUSTOMER_INTEGRATIONS_ERROR_INVALID_DATA'),
        {
          variant: 'error',
          autoHideDuration: 5000
        }
      );
      return;
    }

    setLoading(true);
    try {
      const response = await Axios.post(
        `${API.integrations}/processed/api`,
        config,
        axiosHeaders(localStorage.getItem('PROCYS_accessToken'))
      );

      if (response.data.success) {
        setConfig(response.data.data || config);
        setIsFieldChanged(false);

        enqueueSnackbar(
          t('CUSTOMER_INTEGRATIONS_DETAILS_SAVED_SUCCESS'),
          {
            variant: 'success',
            autoHideDuration: 5000
          }
        );
        setLoading(false);
      } else {
        setLoading(false);
        enqueueSnackbar(
          appendContactSupport(window.config.support_email, t('SOMETHING_WENT_WRONG'), t),
          {
            variant: 'error',
            autoHideDuration: 5000
          }
        );
      }
    } catch (error) {
      setLoading(false);
      let errMsg = t('SOMETHING_WENT_WRONG');
      if (error?.response?.data) {
        errMsg = getLocalisedErrorString(error.response.data.i18n, t);
      }
      enqueueSnackbar(
        appendContactSupport(window.config.support_email, errMsg, t),
        {
          variant: 'error',
          autoHideDuration: 5000
        }
      );
    }
  };

  const isValidUrl = (url) => {
    try {
      const u = new URL(url);
      return u.protocol === 'http:' || u.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const isFormValid = () => {
    if (!config || !config.url || !isValidUrl(config.url)) return false;
    switch (config.authorizationType) {
      case 'basic-auth':
        return config.username && config.password;
      case 'bearer-token':
        return !!config.bearerToken;
      case 'api-key':
        return config.key && config.value;
      default:
        return false;
    }
  };

  const generateExampleBody = () => {
    const metadata = {
      id: 123456,
      assistantID: 789012,
      email: user?.email || "user@example.com",
      company: user?.company || "Test Company",
      creationTime: 1715836800000,
      documentFile: "test-invoice.pdf",
      fileName: "test-invoice.pdf",
      modifiedBy: user?.email || "user@example.com",
      modifiedTime: 1715836800000,
      owner: user?.email || "user@example.com",
      processedDocType: user?.documentType || "INVOICE",
      status: 500
    };

    const headerObj = {};
    headerFields.filter(f => f.isAvailable).forEach(f => {
      if (f.key === 'isStampAvailable') headerObj[f.key] = false;
      else headerObj[f.key] = t(f.label || f.key);
    });

    let linesArr;
    const availableLines = lineFields.filter(f => f.isAvailable);
    if (availableLines.length > 0) {
      const lineObj = {};
      availableLines.forEach(f => {
        lineObj[f.key] = t(f.label || f.key);
      });
      linesArr = [lineObj];
    }

    let vatArr;
    const vatHeader = headerFields.find(f => f.key === 'vatAmount' && f.isAvailable && f.isActive);
    if (vatHeader) {
      const vatObj = {};
      vatLineFields.forEach(f => {
        vatObj[f.key] = t(f.label || f.key);
      });
      vatObj.line = Math.floor(Math.random() * 100) + 1;
      vatArr = [vatObj];
    }

    let esArr;
    const eqField = headerFields.find(f => f.key === 'equivalenceSurchargeAmount' && f.isAvailable && f.isActive);
    if (eqField) {
      const esObj = {};
      esLineFields.forEach(f => {
        esObj[f.key] = t(f.label || f.key);
      });
      esObj.line = Math.floor(Math.random() * 100) + 1;
      esArr = [esObj];
    }

    const requestBody = {
      ...metadata,
      documentType: t(user?.documentType || "INVOICE")
    };
    Object.assign(requestBody, headerObj);
    if (linesArr) {
      requestBody.lines = linesArr;
    }
    if (vatArr) {
      requestBody.vat = vatArr;
    }
    if (esArr) {
      requestBody.es = esArr;
    }

    return requestBody;
  };

  const generateExampleRequest = () => {
    const requestBody = generateExampleBody();

    let authHeader = '';
    switch (config.authorizationType) {
      case 'basic-auth':
        authHeader = `-u "${config.username}:${config.password}"`;
        break;
      case 'bearer-token':
        authHeader = `-H "Authorization: Bearer ${config.bearerToken}"`;
        break;
      case 'api-key':
        authHeader = `-H "${config.key}: ${config.value}"`;
        break;
    }

    const curlCommand = `curl -X ${config.method} \\
      ${authHeader} \\
      -H "Content-Type: application/json" \\
      -d '${JSON.stringify(requestBody, null, 2)}' \\
      "${config.url || 'https://your-api-endpoint.com/documents'}"`;

    setExampleRequest(curlCommand);
    setOpenExampleDrawer(true);
  }

  const onCheckConnection = async () => {
    if (!isFormValid()) {
      enqueueSnackbar(
        t('CUSTOMER_INTEGRATIONS_PROVIDE_ALL_DETAILS'),
        {
          variant: 'warning',
          autoHideDuration: 5000
        }
      );
      return;
    }

    setLoading(true);
    try {
      let headers = {
        'Content-Type': 'application/json'
      };

      switch (config.authorizationType) {
        case 'basic-auth':
          headers['Authorization'] = `Basic ${btoa(`${config.username}:${config.password}`)}`;
          break;
        case 'bearer-token':
          headers['Authorization'] = `Bearer ${config.bearerToken}`;
          break;
        case 'api-key':
          headers[config.key] = config.value;
          break;
      }

      const response = await Axios({
        method: config.method || 'POST',
        url: config.url,
        headers: headers,
        data: generateExampleBody()
      });

      if (response.status >= 200 && response.status < 300) {
        enqueueSnackbar(
          t('CUSTOMER_INTEGRATIONS_ENDPOINT_CONN_SUCCESS'),
          {
            variant: 'success',
            autoHideDuration: 5000
          }
        );
      } else {
        throw new Error(`Unexpected response: ${response.status}`);
      }

      setLoading(false);
    } catch (error) {
      setLoading(false);
      let errMsg;

      if (error.message && error.message.toLowerCase() === 'network error') {
        errMsg = t('CUSTOMER_INTEGRATIONS_CONNECTION_FAILED_NETWORK_OR_CORS');
      } else if (error.response) {
        errMsg = t('CUSTOMER_INTEGRATIONS_CONNECTION_FAILED_WITH_STATUS', { status: error.response.status });
      } else if (error.request) {
        errMsg = t('CUSTOMER_INTEGRATIONS_CONNECTION_FAILED_NO_RESPONSE');
      } else {
        errMsg = t('CUSTOMER_INTEGRATIONS_CONNECTION_FAILED');
      }

      enqueueSnackbar(
        errMsg,
        {
          variant: 'error',
          autoHideDuration: 5000
        }
      );
    }
  };

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(exampleRequest);
    setCopySuccess(true);
    enqueueSnackbar(
      t('CUSTOMER_COPIED_TO_CLIPBOARD'),
      {
        variant: 'success',
        autoHideDuration: 2000
      }
    );

    setTimeout(() => {
      setCopySuccess(false);
    }, 2000);
  };

  if (appError) {
    return (
      <Grid style={styles.errorContainer}>
        <span style={styles.labelTextError}>
          {appError}
        </span>
      </Grid>
    );
  }

  return (
    <Grid style={styles.customerApiDetailsContainer}>
      <Grid style={styles.detailRow}>
        <Grid style={styles.longlabelWrapper}>
          <span style={styles.labelText}>{ready && t('CUSTOMER_INTEGRATIONS_REQUEST_METHOD')}</span>
        </Grid>
        <div style={styles.customInputContainerStyle}>
          <select
            name="method"
            value={config?.method || 'POST'}
            style={styles.inputField}
            className="export-email-style"
            onChange={handleConfigChange}
            disabled={loading}
          >
            <option value="POST">POST</option>
            <option value="PUT">PUT</option>
          </select>
        </div>
      </Grid>

      <Grid style={styles.detailRow}>
        <Grid style={styles.longlabelWrapper}>
          <span style={styles.labelText}>{ready && t('CUSTOMER_INTEGRATIONS_REQUEST_ENDPOINT')}</span>
        </Grid>
        <div style={{ ...styles.customInputContainerStyle, display: 'flex', flexDirection: 'column' }}>
          <input
            type="text"
            name="url"
            value={config?.url || ''}
            style={{
              ...styles.inputField,
              borderColor: config.url && !isValidUrl(config.url) ? '#d32f2f' : styles.inputField.borderColor
            }}
            onChange={handleConfigChange}
            disabled={loading}
            placeholder="https://example.com/api/documents"
          />
          {config.url && !isValidUrl(config.url) && (
            <span style={{ color: '#d32f2f', fontSize: 12, marginTop: 2, marginLeft: 8 }}>{t('CUSTOMER_INTEGRATIONS_INVALID_URL')}</span>
          )}
        </div>
      </Grid>

      <Grid style={styles.detailRow}>
        <Grid style={styles.longlabelWrapper}>
          <span style={styles.labelText}>{ready && t('CUSTOMER_INTEGRATIONS_AUTH_TYPE')}</span>
        </Grid>
        <div style={styles.customInputContainerStyle}>
          <select
            name="authorizationType"
            value={config?.authorizationType || 'basic-auth'}
            style={styles.inputField}
            className="export-email-style"
            onChange={handleConfigChange}
            disabled={loading}
          >
            <option value="basic-auth">Basic Auth</option>
            <option value="bearer-token">Bearer Token</option>
            <option value="api-key">API Key</option>
          </select>
        </div>
      </Grid>

      {config?.authorizationType === 'basic-auth' && (
        <>
          <Grid style={styles.detailRow}>
            <Grid style={styles.longlabelWrapper}>
              <span style={styles.labelText}>{ready && t('CUSTOMER_INTEGRATIONS_USERNAME')}</span>
            </Grid>
            <div style={styles.customInputContainerStyle}>
              <input
                type="text"
                name="username"
                value={config?.username || ''}
                style={styles.inputField}
                onChange={handleConfigChange}
                disabled={loading}
              />
            </div>
          </Grid>

          <Grid style={styles.detailRow}>
            <Grid style={styles.longlabelWrapper}>
              <span style={styles.labelText}>{ready && t('CUSTOMER_INTEGRATIONS_PASSWORD')}</span>
            </Grid>
            <div style={styles.customInputContainerStyle}>
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={config?.password || ''}
                style={styles.inputField}
                onChange={handleConfigChange}
                disabled={loading}
              />
              <div style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)' }}>
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                  size="small"
                >
                  {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </div>
            </div>
          </Grid>
        </>
      )}

      {config?.authorizationType === 'bearer-token' && (
        <Grid style={styles.detailRow}>
          <Grid style={styles.longlabelWrapper}>
            <span style={styles.labelText}>{ready && t('CUSTOMER_INTEGRATIONS_BEARER_TOKEN')}</span>
          </Grid>
          <div style={styles.customInputContainerStyle}>
            <input
              type={showToken ? 'text' : 'password'}
              name="bearerToken"
              value={config?.bearerToken || ''}
              style={styles.inputField}
              onChange={handleConfigChange}
              disabled={loading}
            />
            <div style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)' }}>
              <IconButton
                onClick={() => setShowToken(!showToken)}
                edge="end"
                size="small"
              >
                {showToken ? <VisibilityOffIcon /> : <VisibilityIcon />}
              </IconButton>
            </div>
          </div>
        </Grid>
      )}

      {config?.authorizationType === 'api-key' && (
        <>
          <Grid style={styles.detailRow}>
            <Grid style={styles.longlabelWrapper}>
              <span style={styles.labelText}>{ready && t('CUSTOMER_INTEGRATIONS_API_KEY_NAME')}</span>
            </Grid>
            <div style={styles.customInputContainerStyle}>
              <input
                type="text"
                name="key"
                value={config?.key || ''}
                style={styles.inputField}
                onChange={handleConfigChange}
                disabled={loading}
                placeholder="X-API-Key"
              />
            </div>
          </Grid>

          <Grid style={styles.detailRow}>
            <Grid style={styles.longlabelWrapper}>
              <span style={styles.labelText}>{ready && t('CUSTOMER_INTEGRATIONS_API_KEY_VALUE')}</span>
            </Grid>
            <div style={styles.customInputContainerStyle}>
              <input
                type={showApiValue ? 'text' : 'password'}
                name="value"
                value={config?.value || ''}
                style={styles.inputField}
                onChange={handleConfigChange}
                disabled={loading}
              />
              <div style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)' }}>
                <IconButton
                  onClick={() => setShowApiValue(!showApiValue)}
                  edge="end"
                  size="small"
                >
                  {showApiValue ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </div>
            </div>
          </Grid>
        </>
      )}

      <Grid style={styles.checkContainer}>
        <Tooltip
          title={!isFormValid() ? t('CUSTOMER_INTEGRATIONS_PROVIDE_ALL_DETAILS') : ''}
          placement="top"
          arrow
          disableHoverListener={isFormValid()}
          disableFocusListener={isFormValid()}
        >
          <span>
            <Button
              variant="outlined"
              style={styles.checkConnectionBtn}
              onClick={onCheckConnection}
              disabled={loading || !isFormValid()}
            >
              {loading && <CircularProgress size={16} style={{ marginRight: 8 }} />}
              {ready && t('CUSTOMER_INTEGRATIONS_CHECK_CONNECTION')}
            </Button>
          </span>
        </Tooltip>

        <div style={{ display: 'flex', gap: '10px' }}>
          <Tooltip
            title={
              !isFormValid()
                ? t('CUSTOMER_INTEGRATIONS_PROVIDE_ALL_DETAILS')
                : ''
            }
            placement="top"
            arrow
            disableHoverListener={isFormValid() && isFieldChanged}
            disableFocusListener={isFormValid() && isFieldChanged}
          >
            <span>
              <Button
                variant="outlined"
                style={styles.syncBtn}
                onClick={saveDetails}
                disabled={loading || !isFormValid() || !isFieldChanged}
                startIcon={<Save />}
              >
                {ready && t('CUSTOMER_INTEGRATIONS_SAVE')}
              </Button>
            </span>
          </Tooltip>

          <Button
            variant="outlined"
            style={styles.syncBtn}
            onClick={generateExampleRequest}
            disabled={loading}
            startIcon={<InfoIcon />}
          >
            {ready && t('CUSTOMER_INTEGRATIONS_VIEW_EXAMPLE_REQUEST')}
          </Button>
        </div>
      </Grid>

      {/* info text */}
      <Grid style={styles.infoContainer}>
        <InfoIcon style={styles.infoIcon} />
        <Grid style={styles.infoTextContainer}>
          <Typography style={styles.infoText}>
            {ready && t('CUSTOMER_INTEGRATIONS_INFO_TEXT')}
          </Typography>
        </Grid>
      </Grid>

      <Drawer
        anchor="right"
        open={openExampleDrawer}
        onClose={() => setOpenExampleDrawer(false)}
        sx={styles.drawerStyles.drawer}
      >
        <Box sx={styles.drawerStyles.container}>
          <Box sx={styles.drawerStyles.modalHeader}>
            <Box sx={styles.drawerStyles.titleContainer}>
              <Typography sx={styles.drawerStyles.headerTitle}>
                {ready && t('CUSTOMER_INTEGRATIONS_EXAMPLE_REQUEST')}
              </Typography>
              <IconButton sx={styles.drawerStyles.closeBtn} onClick={() => setOpenExampleDrawer(false)}>
                <CloseIcon sx={styles.drawerStyles.closeIcon} />
              </IconButton>
            </Box>
          </Box>

          <Box sx={styles.drawerStyles.modalContent}>
            <Typography sx={styles.drawerStyles.contentNote}>
              {ready && t('CUSTOMER_INTEGRATIONS_EXAMPLE_REQUEST_NOTE')}
            </Typography>

            <Box sx={styles.drawerStyles.jsonContainer}>
              <pre style={styles.drawerStyles.jsonPre}>
                {exampleRequest}
              </pre>
              <Box sx={styles.drawerStyles.copyIconContainer}>
                <IconButton sx={styles.drawerStyles.copyBtn} onClick={handleCopyToClipboard}>
                  <ContentCopyIcon sx={styles.drawerStyles.copyIcon} />
                </IconButton>
                {copySuccess && <CheckCircleOutline sx={styles.drawerStyles.checkIcon} />}
              </Box>
            </Box>
          </Box>
        </Box>
      </Drawer>
    </Grid>
  );
};

export default NotifyProcessedDoc;
